import { adminGet, adminPost } from './useAdminCore';
export const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
// ==================== Pure helpers ====================
// Rule 2: an operator typing a Title that already exists must be prompted to link or rename
function findTitleCollision(title, threads) {
    const needle = (title || '').trim().toLowerCase();
    if (!needle)
        return null;
    return threads.find(t => (t.title || '').trim().toLowerCase() === needle) || null;
}
// Rule 6: reply to an inbound non-verbal contact = outbound contact on the same thread, same method
function buildReplyContact(saved) {
    return {
        thread: saved.thread,
        contactMethod: saved.contactMethod,
        contactReason: saved.contactReason,
        inbound: false,
    };
}
// A thread's outstanding follow-up, or null if there is none.
// Rule 4 (server stamps FollowedUp when a new contact lands on the same thread) isn't
// implemented in Create yet, so we derive it: any later contact fulfils an earlier
// follow-up, meaning only the newest contact can still hold an open one.
// Expects contacts oldest first, as returned by groupContactsIntoThreads.
// ponytail: derived client-side; drop this once Create stamps FollowedUp itself
function openFollowUp(contacts) {
    const latest = contacts[contacts.length - 1];
    return latest && latest.followUpDue && !latest.followedUp ? latest : null;
}
// Derived status (no server field yet): an unfulfilled follow-up keeps a thread open;
// otherwise a thread whose latest contact is inbound is awaiting our reply.
// Expects contacts oldest first, as returned by groupContactsIntoThreads.
function threadStatus(contacts) {
    if (openFollowUp(contacts))
        return 'open';
    const latest = contacts[contacts.length - 1];
    if (latest && latest.inbound)
        return 'pending';
    return 'closed';
}
// Countdown against a due date: "2d 4h left" or "5h 20m over"
function slaCountdown(due, now = new Date()) {
    const ms = new Date(due).getTime() - now.getTime();
    const abs = Math.abs(ms);
    const days = Math.floor(abs / 86400000);
    const hours = Math.floor((abs % 86400000) / 3600000);
    const minutes = Math.floor((abs % 3600000) / 60000);
    const span = days > 0 ? `${days}d ${hours}h` : hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
    return ms >= 0 ? { label: span + ' left', breached: false } : { label: span + ' over', breached: true };
}
// Group contacts by their thread, oldest first within each group.
// A contact with no thread (bad data) becomes its own single-contact group.
function groupContactsIntoThreads(contacts) {
    const groups = new Map();
    for (const contact of contacts) {
        const thread = contact.thread || { id: contact.id, title: '' };
        if (!groups.has(thread.id))
            groups.set(thread.id, { thread, contacts: [] });
        groups.get(thread.id).contacts.push(contact);
    }
    return [...groups.values()].map(group => ({
        ...group,
        contacts: group.contacts.sort((a, b) => a.contactDateTime.localeCompare(b.contactDateTime)),
    }));
}
// ==================== API ====================
function crmBase(entityType, entityId) {
    return (entityType === 'employer' ? '/Employers/' : '/Customers/') + entityId + '/CRM';
}
function getCrmContacts(entityType, entityId, onSuccess = null) {
    adminGet(crmBase(entityType, entityId) + '/Contacts', onSuccess);
}
function getCrmThreads(entityType, entityId, onSuccess = null) {
    adminGet(crmBase(entityType, entityId) + '/Threads', onSuccess);
}
function getCrmContact(contactId, onSuccess = null) {
    adminGet('/CRM/Contacts/' + contactId, onSuccess);
}
function createCrmThread(title, onSuccess = null, onError = null) {
    adminPost('/CRM/Threads', { title }, onSuccess, () => {
        if (onError)
            onError('Failed to create thread');
    });
}
function createCrmContact(entityType, entityId, contact, onSuccess = null, onError = null) {
    adminPost(crmBase(entityType, entityId) + '/Contacts', contact, onSuccess, () => {
        if (onError)
            onError('Failed to log contact');
    });
}
function getCrmContactMethods(onSuccess = null) {
    adminGet('/CRM/ContactMethods', onSuccess);
}
function getCrmContactReasons(onSuccess = null) {
    adminGet('/CRM/ContactReasons', onSuccess);
}
// Passing the empty guid makes the server resolve the current user's customer ID
function getCrmWorkList(onSuccess = null) {
    adminGet('/Operators/' + EMPTY_GUID + '/CRM/Worklist', onSuccess);
}
// No operator list exists yet — operators will be a new flag on the user entity.
// Until it lands, follow-ups can only be assigned to the current operator.
function getCrmOperators(onSuccess = null) {
    if (onSuccess)
        onSuccess([]);
}
export { findTitleCollision, buildReplyContact, groupContactsIntoThreads, openFollowUp, threadStatus, slaCountdown, getCrmContacts, getCrmThreads, getCrmContact, createCrmThread, createCrmContact, getCrmContactMethods, getCrmContactReasons, getCrmOperators, getCrmWorkList, };
